import { useCallback, useMemo, useRef, useState } from 'react';
import { Select, Spin } from 'antd';
import { searchInstitutions } from '@/api/collect';
import type { FormValues, SchemaField } from '@/domain/schemaForm';

/** DYNAMIC_SELECT 字段（典型：美国 ACH 的 routing number 搜银行）。选项要按关键词去查。 */
export default function DynamicSelect({
  token,
  collectToken,
  field,
  value,
  values,
  disabled,
  onChange,
}: {
  token: string;
  collectToken: string | null;
  field: SchemaField;
  value: string | undefined;
  values: FormValues;
  disabled?: boolean;
  onChange: (v: string) => void;
}) {
  const [options, setOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [loading, setLoading] = useState(false);
  /** 防抖 + 丢弃过期响应：慢的那一发回来时不能覆盖新的结果。 */
  const seq = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const keywordPattern = useMemo(() => {
    const p = field.field.dynamic_options?.query_params.find((q) => q.name === 'keyword')?.pattern;
    if (!p) return null;
    try {
      return new RegExp(p);
    } catch {
      return null;
    }
  }, [field]);

  const doSearch = useCallback(
    async (keyword: string) => {
      if (keywordPattern && !keywordPattern.test(keyword)) {
        setOptions([]);
        return;
      }
      const mine = ++seq.current;
      setLoading(true);
      try {
        const res = await searchInstitutions(token, collectToken, {
          keyword,
          bank_country_code: values['beneficiary.bank_details.bank_country_code'] ?? '',
          account_currency: values['beneficiary.bank_details.account_currency'] ?? '',
          transfer_method: values['transfer_method'] ?? values['transfer_methods'] ?? '',
          // ⚠️ entity_type 必传，但 schema 的 query_params 里并没有列出来
          entity_type: values['beneficiary.entity_type'] ?? '',
          local_clearing_system: values['beneficiary.bank_details.local_clearing_system'] || undefined,
        });
        if (mine === seq.current) setOptions(res.items);
      } catch {
        if (mine === seq.current) setOptions([]);
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    },
    [keywordPattern, token, collectToken, values],
  );

  return (
    <Select
      showSearch
      allowClear
      size="large"
      disabled={disabled}
      value={value || undefined}
      placeholder={field.field.placeholder || 'Type to search'}
      filterOption={false}
      onSearch={(kw) => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => void doSearch(kw), 300);
      }}
      onChange={(v) => onChange(v ?? '')}
      notFoundContent={loading ? <Spin size="small" /> : null}
      options={options}
      style={{ width: '100%' }}
    />
  );
}
